import React, {
  useState,
  useEffect,
  useCallback,
  useImperativeHandle,
  forwardRef,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import { useMediaQuery, useTheme, Box } from '@mui/material';
import Calendar from 'rc-year-calendar';
import 'rc-year-calendar/locales/rc-year-calendar.es';
import 'rc-year-calendar/locales/rc-year-calendar.de';
import 'rc-year-calendar/locales/rc-year-calendar.fr';
import 'rc-year-calendar/locales/rc-year-calendar.pt';
import { fetchOccupancyData, fetchMyPastBookings } from '../api/bookings';
import { useTranslation } from 'react-i18next';
import { fetchConfigurations } from '../api/configurations';
import { getOccupancyColor } from '../utils';

const CalendarView = forwardRef(({ onDayClick, setSnackbar, dataSource, showUserPastBookings }, ref) => {
  const { i18n } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const calendarContainerRef = useRef(null);
  
  const [occupancyData, setOccupancyData] = useState([]);
  const [holidayData, setHolidayData] = useState([]);
  const [myPastBookings, setMyPastBookings] = useState([]);
  const [calendarYear, setCalendarYear] = useState(new Date().getFullYear());
  const [thresholds, setThresholds] = useState({ high: 75, medium: 50 });
  const [maxAdvanceDays, setMaxAdvanceDays] = useState(30);

  const loadThresholds = useCallback(async () => {
    try {
      const data = await fetchConfigurations();
      setThresholds({
        high: parseFloat(data.HIGH_OCCUPANCY || 75),
        medium: parseFloat(data.MEDIUM_OCCUPANCY || 50),
      });
      setMaxAdvanceDays(parseInt(data.MAX_ADVANCE_DAYS || 30));
    } catch (err) {
      console.error('Error fetching configuration thresholds:', err);
    }
  }, []);

  const getDateLimits = useCallback(() => {
    const today = new Date();
    const currentMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const maxBookableDate = new Date(today);
    maxBookableDate.setDate(today.getDate() + maxAdvanceDays);
    const maxMonth = new Date(maxBookableDate.getFullYear(), maxBookableDate.getMonth() + 1, 0);
    return {
      minDate: currentMonth,
      maxDate: maxMonth,
      currentMonth: today.getMonth(),
      currentYear: today.getFullYear(),
      maxMonth: maxBookableDate.getMonth(),
      maxYear: maxBookableDate.getFullYear()
    };
  }, [maxAdvanceDays]);

  const prepareCalendarData = () => {
    if (!occupancyData || typeof occupancyData !== 'object') return dataSource || [];

    const occupancyDays = Object.keys(occupancyData || {}).map((date) => {
      const percent = occupancyData[date]?.percent || 0;
      return {
        startDate: new Date(date),
        endDate: new Date(date),
        color: getOccupancyColor(percent, thresholds, false),
        name: `${percent}% Occupancy`,
      };
    });

    const holidayDays = holidayData.map((date) => ({
      startDate: new Date(date),
      endDate: new Date(date),
      color: 'lightgray',
      name: 'Bank Holiday',
    }));

    const pastBookings = myPastBookings.map((date) => ({
      startDate: new Date(date),
      endDate: new Date(date),
      color: 'lightgreen',
      name: 'Past Bookings',
    }));

    const localData = [...occupancyDays, ...holidayDays, ...pastBookings];

    // Ensure user overlays (bookings/releases) render on top by appending them last
    return dataSource ? [...localData, ...dataSource] : localData;
  };

  const loadOccupancyData = useCallback(async () => {
    try {
      const data = await fetchOccupancyData(calendarYear);
      setHolidayData(data.bank_holidays || []);
      setOccupancyData(data.occupancy || {});
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    }
  }, [calendarYear, setSnackbar]);

  const loadMyPastBookings = useCallback(async () => {
    try {
      const data = await fetchMyPastBookings(calendarYear);
      setMyPastBookings(data || []);
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    }
  }, [calendarYear, setSnackbar]);

  const handleYearChanged = (e) => {
    const { minDate, maxDate } = getDateLimits();
    if (minDate && maxDate) {
      const minYear = minDate.getFullYear();
      const maxYear = maxDate.getFullYear();
      const requestedYear = e.currentYear;
      if (requestedYear < minYear) { setCalendarYear(minYear); return; }
      if (requestedYear > maxYear) { setCalendarYear(maxYear); return; }
    }
    setCalendarYear(e.currentYear);
  };

  useEffect(() => { loadThresholds(); }, [loadThresholds]);
  useEffect(() => { loadOccupancyData(); if (showUserPastBookings) { loadMyPastBookings(); } }, [calendarYear, showUserPastBookings, loadOccupancyData, loadMyPastBookings]);

  const hideOutOfScopeMonths = useCallback(() => {
    if (!isMobile || !calendarContainerRef.current) return;
    const { currentMonth, currentYear, maxMonth, maxYear } = getDateLimits();
    let monthContainers = calendarContainerRef.current.querySelectorAll('.month-container');
    if (monthContainers.length === 0) { monthContainers = calendarContainerRef.current.querySelectorAll('.month'); }
    if (monthContainers.length === 0) { monthContainers = calendarContainerRef.current.querySelectorAll('[class*="month"]'); }
    monthContainers.forEach((container, index) => {
      let shouldHide = false;
      if (calendarYear === currentYear) {
        if (index < currentMonth) shouldHide = true;
        if (maxYear === currentYear && index > maxMonth) shouldHide = true;
      } else if (calendarYear === maxYear && maxYear > currentYear) {
        if (index > maxMonth) shouldHide = true;
      } else if (calendarYear < currentYear || calendarYear > maxYear) {
        shouldHide = true;
      }
      container.style.display = shouldHide ? 'none' : '';
      container.style.visibility = shouldHide ? 'hidden' : '';
    });
  }, [isMobile, calendarYear, getDateLimits]);

  const showAllMonthsOnDesktop = useCallback(() => {
    if (isMobile || !calendarContainerRef.current) return;
    let monthContainers = calendarContainerRef.current.querySelectorAll('.month-container');
    if (monthContainers.length === 0) { monthContainers = calendarContainerRef.current.querySelectorAll('.month'); }
    if (monthContainers.length === 0) { monthContainers = calendarContainerRef.current.querySelectorAll('[class*="month"]'); }
    monthContainers.forEach((container) => {
      container.style.display = '';
      container.style.visibility = '';
    });
  }, [isMobile]);

  const handleRenderEnd = useCallback(() => {
    setTimeout(() => { if (isMobile) { hideOutOfScopeMonths(); } else { showAllMonthsOnDesktop(); } }, 100);
  }, [isMobile, hideOutOfScopeMonths, showAllMonthsOnDesktop]);

  useEffect(() => {
    const handleVisibility = () => { if (isMobile) { setTimeout(hideOutOfScopeMonths, 50); setTimeout(hideOutOfScopeMonths, 200); } else { showAllMonthsOnDesktop(); } };
    if (isMobile) { handleVisibility(); } else { showAllMonthsOnDesktop(); }
    const existingDisabledStyle = document.getElementById('disabled-dates-color-style');
    if (existingDisabledStyle) { existingDisabledStyle.remove(); }
    const disabledStyle = document.createElement('style');
    disabledStyle.id = 'disabled-dates-color-style';
    disabledStyle.innerHTML = `
      .calendar-container .calendar .day.disabled { opacity: 0.6 !important; cursor: not-allowed !important; }
      .calendar-container .calendar .day.disabled.day-content { background-color: inherit !important; }
      .calendar-container .calendar .day.disabled[style*="background-color"] { background-color: inherit !important; opacity: 0.6 !important; }
      .calendar-container .calendar .day.disabled .day-content { color: #333 !important; font-weight: 500 !important; }
    `;
    document.head.appendChild(disabledStyle);
    if (isMobile) {
      const existingStyle = document.getElementById('mobile-calendar-layout-style');
      if (existingStyle) existingStyle.remove();
      const style = document.createElement('style');
      style.id = 'mobile-calendar-layout-style';
      style.innerHTML = `
        @media (max-width: ${theme.breakpoints.values.md}px) {
          .calendar-container .calendar .months-container { display: flex !important; flex-direction: column !important; gap: 15px !important; align-items: center !important; }
          .calendar-container .calendar .months-container .month-container:not([style*="display: none"]) { width: 100% !important; max-width: 350px !important; margin: 0 !important; float: none !important; position: relative !important; display: block !important; margin-bottom: 10px !important; }
          .calendar-container .calendar .month-container:not([style*="display: none"]) .month-header { text-align: center !important; margin-bottom: 10px !important; }
          .calendar-container .calendar .month-container:not([style*="display: none"]) .days-container { width: 100% !important; }
          .calendar-container .calendar .calendar-header { text-align: center !important; margin-bottom: 20px !important; }
        }
      `;
      document.head.appendChild(style);
      return () => {
        const styleToRemove = document.getElementById('mobile-calendar-layout-style');
        if (styleToRemove) styleToRemove.remove();
        const disabledStyleToRemove = document.getElementById('disabled-dates-color-style');
        if (disabledStyleToRemove) disabledStyleToRemove.remove();
      };
    } else {
      const existingStyle = document.getElementById('mobile-calendar-layout-style');
      if (existingStyle) existingStyle.remove();
    }
    return () => { const disabledStyleToRemove = document.getElementById('disabled-dates-color-style'); if (disabledStyleToRemove) disabledStyleToRemove.remove(); };
  }, [isMobile, hideOutOfScopeMonths, showAllMonthsOnDesktop, theme.breakpoints.values.md]);

  useImperativeHandle(ref, () => ({ refreshCalendarData: loadOccupancyData }));

  const { minDate, maxDate } = getDateLimits();

  return (
    <Box className="calendar-container" ref={calendarContainerRef}>
      <Calendar
        disabledWeekDays={[6, 0]}
        year={calendarYear}
        onDayClick={onDayClick}
        language={i18n.language}
        dataSource={prepareCalendarData()}
        onYearChanged={handleYearChanged}
        onRenderEnd={handleRenderEnd}
        minDate={isMobile ? minDate : null}
        maxDate={isMobile ? maxDate : null}
        displayDisabledDataSource={true}
        // eslint-disable-next-line
        style={"background"}
        weekStart={1}
      />
    </Box>
  );
});

CalendarView.propTypes = {
  onDayClick: PropTypes.func,
  setSnackbar: PropTypes.func.isRequired,
  dataSource: PropTypes.arrayOf(
    PropTypes.shape({ startDate: PropTypes.instanceOf(Date).isRequired, endDate: PropTypes.instanceOf(Date).isRequired, color: PropTypes.string, name: PropTypes.string })
  ),
  userPastBookings: PropTypes.bool
};

export default CalendarView;
